// Tab navigation - matching GroupContainer structure
export const ALL_GROUPS_TABS: Record<
  string,
  {
    id: string
    route: string
    isAdminOnly?: boolean
    url?: string
  }
> = {
  home: {
    id: 'home',
    route: 'communities-groups-home',
    url: '/home',
  },
  learning: {
    id: 'learning',
    route: 'communities-groups-learning',
    url: '/learning',
  },
  events: {
    id: 'events',
    route: 'communities-groups-events',
    url: '/events',
  },
  leaderboard: {
    id: 'leaderboard',
    route: 'communities-groups-leaderboard',
    url: '/leaderboard',
  },
  members: {
    id: 'members',
    route: 'communities-groups-members',
    url: '/members',
  },
  about: {
    id: 'about',
    route: 'communities-groups-about',
    url: '/about',
  },
  settings: {
    id: 'settings',
    route: 'communities-groups-settings-details',
    isAdminOnly: true,
    url: '/settings',
  },
} as const

// Tabs that are always visible regardless of configuration
export const MANDATORY_GROUP_TAB_IDS = ['home', 'about'] as const

// Tabs that can be toggled on/off by admins
export const OPTIONAL_GROUP_TAB_IDS = ['learning', 'events', 'leaderboard', 'members'] as const

// Frontend tab ID → backend enum mapping
export const TAB_ID_TO_BACKEND_ENUM: Record<string, string> = {
  home: 'DISCUSSION',
  learning: 'LEARNING',
  members: 'MEMBERS',
  events: 'EVENTS',
  leaderboard: 'LEADERBOARD',
  about: 'ABOUT',
} as const

// Backend enum → frontend tab ID mapping (reverse of above)
export const BACKEND_ENUM_TO_TAB_ID: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_ID_TO_BACKEND_ENUM).map(([tabId, backendEnum]) => [backendEnum, tabId])
)

/** Returns all tab IDs as the default when tabsConfiguration is undefined */
export function getDefaultEnabledTabIds(): string[] {
  return [...MANDATORY_GROUP_TAB_IDS, ...OPTIONAL_GROUP_TAB_IDS]
}

/** Converts frontend tab IDs to backend enum values */
export function tabIdsToBackendEnums(tabIds: string[]): string[] {
  return tabIds.map(id => TAB_ID_TO_BACKEND_ENUM[id]).filter(Boolean)
}

/** Converts backend enum values to frontend tab IDs */
export function backendEnumsToTabIds(enums: string[]): string[] {
  return enums.map(e => BACKEND_ENUM_TO_TAB_ID[e]).filter(Boolean)
}

/** Backend tab order for tabsConfiguration array (matches API) */
const TABS_CONFIG_ORDER: string[] = ['DISCUSSION', 'LEARNING', 'EVENTS', 'LEADERBOARD', 'MEMBERS', 'ABOUT']

/**
 * Normalize tabsConfiguration from API (array of strings or array of { name, isVisible })
 * to backend enum names that are visible.
 * When config is array of objects: tabs explicitly listed with isVisible true are visible;
 * any tab whose name is not in the config at all is treated as visible (ON by default).
 */
export function getVisibleTabEnums(config: Array<{ name: string; isVisible: boolean }> | string[] | undefined): string[] {
  if (!config || config.length === 0) return TABS_CONFIG_ORDER
  const first = config[0]
  if (typeof first === 'string') {
    return config as string[]
  }
  const items = config as Array<{ name: string; isVisible: boolean }>
  const namesInConfig = new Set(items.map(item => item.name))
  const explicitlyVisible = items.filter(item => item.isVisible).map(item => item.name)
  const missingFromConfig = TABS_CONFIG_ORDER.filter(name => !namesInConfig.has(name))
  return [...new Set([...explicitlyVisible, ...missingFromConfig])]
}

/**
 * Build tabsConfiguration payload for API: array of { name, isVisible } in backend order.
 */
export function buildTabsConfigurationPayload(enabledTabIds: Set<string>): Array<{ name: string; isVisible: boolean }> {
  return TABS_CONFIG_ORDER.map(name => ({
    name,
    isVisible: enabledTabIds.has(BACKEND_ENUM_TO_TAB_ID[name] ?? ''),
  }))
}

/**
 * Keys from group.settings to preserve when updating only tabs (matches IGroupSettings).
 * Used so we send only known settings and never forward unknown/legacy API keys.
 */
const SETTINGS_KEYS_TO_PRESERVE_ON_TABS_UPDATE = [
  'groupType',
  'membershipQuestionsEnabled',
  'groupSwitcherVisibility',
  'allowsMembersInvite',
  'formId',
  'gamificationSettings',
  'membership',
  'isDiscoverable',
  'newsletter',
  'customCode',
] as const

/**
 * Pick only known settings keys to send when updating tabs. Excludes any unknown keys from the API.
 */
export function pickSettingsForTabsUpdate(settings: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!settings) return {}
  return Object.fromEntries(SETTINGS_KEYS_TO_PRESERVE_ON_TABS_UPDATE.filter(k => settings[k] !== undefined).map(k => [k, settings[k]]))
}
